import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from './index';
import {
  CoursesTextbookVersions_1Res,
  GetPaperDetailParams,
  GetPaperDetailRes,
  getXkwGrades,
  getXkwPapersGetByGenerateRecordId,
  getXkwPapersGetPaperDetail,
  getXkwPapersPreview,
  GetXkwPapersPreviewRes,
  getXkwPaperTypes,
  getXkwQuestionsListByGenerateRecordId,
  getXkwQuestionsListChangeForStudySkills,
  getXkwQuestionsListForStudySkills,
  getXkwSubjects,
  getXkwTextbookVersionsByGradeIdAndSubjectId,
  PapersTypesItem,
} from '../API/mylearn';
import {ListForStudySkillsRes} from './resTypes';
import {userSlice} from './user';

export interface Subject {
  id: number;
  name: string;
  ordinal: number;
}

export interface Grade {
  id: number;
  name: string;
}

// 定义 slice state 的类型
interface XuekeState {
  subjects: Subject[];
  grades: Grade[];
  questions: {data: ListForStudySkillsRes[]; generateRecordId: number};
  versions: CoursesTextbookVersions_1Res[];
  paperTypes: PapersTypesItem[];
  paperQuestions: GetPaperDetailRes;
  parperPreviewQuestions: GetXkwPapersPreviewRes;
}

// 使用该类型定义初始 state

const initialState: XuekeState = {
  subjects: [],
  grades: [],
  // @ts-ignore
  questions: [],
  versions: [],
  paperTypes: [],
  // @ts-ignore
  paperQuestions: {},
};
// 获取学科列表
export const fetchSubjects = createAsyncThunk(
  'xkw/fetchSubjects',
  async (gradeId: number) => {
    const response = await getXkwSubjects({gradeId});
    // 返回 response.data，将由 redux 传递给 'fetchSubjects.fulfilled' 的处理
    return response.data;
  },
);
// 获取年级列表
export const fetchGrades = createAsyncThunk('xkw/fetchGrades', async () => {
  const res = await getXkwGrades();
  return res.data;
});
export const fetchQuestions = createAsyncThunk(
  'xkw/fetchQuestions',
  async (p: any) => {
    const res = await getXkwQuestionsListForStudySkills(p);
    return res.data;
  },
);
export const fetchQuestionsChange = createAsyncThunk(
  'xkw/fetchQuestionsChange',
  async (p: any) => {
    const res = await getXkwQuestionsListChangeForStudySkills(p);
    return res.data;
  },
);
export const fetchBookVersions = createAsyncThunk(
  'xkw/fetchBookVersions',
  async (p: {gradeId: number; subjectId: number}) => {
    const res = await getXkwTextbookVersionsByGradeIdAndSubjectId(p);
    return res.data;
  },
);
export const fetchQuestionsByRecordId = createAsyncThunk(
  'xkw/fetchQuestionsByRecordId',
  async (p: {generateRecordId: number}) => {
    const res = await getXkwQuestionsListByGenerateRecordId(p);
    return res.data;
  },
);
export const fetchPaperTypes = createAsyncThunk(
  'xkw/fetchPaperTypes',
  async () => {
    const res = await getXkwPaperTypes();
    return res.data;
  },
);
export const fetchPaperDetails = createAsyncThunk(
  'xkw/fetchPaperDetails',
  async (p: GetPaperDetailParams) => {
    const res = await getXkwPapersGetPaperDetail(p);
    return res.data;
  },
);
export const fetchPaperPreview = createAsyncThunk(
  'xkw/fetchPaperPreview',
  async (p: {paperId: string}) => {
    const res = await getXkwPapersPreview(p);
    return res.data;
  },
);
// 获取试卷记录详情
export const fetchPaperRecordDetails = createAsyncThunk(
  'xkw/fetchPaperRecordDetails',
  async (p: {generateRecordId: number}) => {
    const res = await getXkwPapersGetByGenerateRecordId(p);
    return res.data;
  },
);
export const xuekeSlice = createSlice({
  name: 'xkw',
  initialState,
  reducers: {
    setQuestions: (state, action) => {
      state.questions = action.payload;
    },
    setTestQuestions: (state, {payload}) => {
      state.questions = {
        data: payload.data,
        generateRecordId: payload.generateReacordId,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSubjects.fulfilled, (state, {payload}) => {
        // 将从 'fetchSubjects' 返回的数组添加到 state
        state.subjects = payload;
      })
      .addCase(fetchGrades.fulfilled, (state, {payload}) => {
        // 将从 'fetchGrades' 返回的数组添加到 state
        state.grades = payload;
      })
      // 新增的fetchQuestions.fulfilled处理
      .addCase(fetchQuestions.fulfilled, (state, {payload}) => {
        state.questions = payload; // 假设你在XuekeState中定义了一个questions属性用于存放该接口返回的数据
      })
      // 新增的fetchQuestions.fulfilled处理
      .addCase(fetchQuestionsChange.fulfilled, (state, {payload}) => {
        state.questions = payload; // 假设你在XuekeState中定义了一个questions属性用于存放该接口返回的数据
      })
      // 新增的fetchQuestions.fulfilled处理
      .addCase(fetchBookVersions.fulfilled, (state, {payload}) => {
        state.versions = payload; // 假设你在XuekeState中定义了一个questions属性用于存放该接口返回的数据
      })
      .addCase(fetchQuestionsByRecordId.fulfilled, (state, action) => {
        state.questions = {
          data: action.payload,
          generateRecordId: action.meta.arg.generateRecordId,
        };
      })
      .addCase(fetchPaperTypes.fulfilled, (state, action) => {
        state.paperTypes = action.payload;
      })
      .addCase(fetchPaperDetails.fulfilled, (state, action) => {
        state.paperQuestions = action.payload;
      })
      .addCase(fetchPaperRecordDetails.fulfilled, (state, action) => {
        state.paperQuestions = action.payload;
      })
      .addCase(fetchPaperPreview.fulfilled, (state, action) => {
        state.parperPreviewQuestions = action.payload;
      });
  },
});
export const {setQuestions, setTestQuestions} = xuekeSlice.actions;

// 所有的 reducer 已经在 'xuekeSlice' 中被定义，因此我们可以这样导出 action： 'export const {...} = xuekeSlice.actions;'
export const selectSubjects = (state: RootState) => state.xkw.subjects;
export const selectGrades = (state: RootState) => state.xkw.grades;
export const selectQuestions = (state: RootState) => state.xkw.questions;
export const selectPaperQuestions = (state: RootState) =>
  state.xkw.paperQuestions;
export const selectBookVersions = (state: RootState) => state.xkw.versions;
export const selectPaperTypes = (state: RootState) => state.xkw.paperTypes;
export const selectPaperPreviewQuestions = (state: RootState) =>
  state.xkw.parperPreviewQuestions;
const xkwReducer = xuekeSlice.reducer;
export default xkwReducer;
